// src/components/orders/wizard/ProductStep.js
import React, { useState } from 'react';
import {
  Package, ArrowRight, ArrowLeft,
  AlertCircle
} from 'lucide-react';
import { useProducts } from '../../../hooks/useApi';


const ProductStep = ({
  data,
  updateData,
  validateStep,
  validations,
  nextStep,
  prevStep,
  invalidateStepsAfter,
  currentStep,
  setCurrentStep,
  updateOrderItems
}) => {

  // Stato per le quantità di ogni prodotto, inizializzato dai prodotti già selezionati
  const [quantities, setQuantities] = useState(() => {
    const initial = {};
    if (data.orderItems && Array.isArray(data.orderItems)) {
      data.orderItems.forEach(item => {
        if (item.product && item.product.id) {
          initial[item.product.id] = item.quantita;
        }
      });
    }
    return initial;
  });
  // const { isOnline } = useOfflineStorage();
  // Carica tutti i prodotti senza filtro
  const { products, loading: productsLoading } = useProducts('');

  // Gestione quantità e aggiornamento ordine
  const handleQuantityChange = (product, newQuantity) => {
    // Aggiorna lo stato delle quantità
    setQuantities((prev) => ({ ...prev, [product.id]: newQuantity }));

    let newItems = [...(data.orderItems || [])];
    const existingItemIndex = newItems.findIndex((item) => item.product.id === product.id);

    if (newQuantity > 0) {
      // Se già presente aggiorna quantità, altrimenti aggiungi
      if (existingItemIndex >= 0) {
        newItems[existingItemIndex].quantita = newQuantity;
        newItems[existingItemIndex].imponibile = newQuantity * newItems[existingItemIndex].prezzo;
      } else {
        const newItem = {
          id: Date.now() + Math.floor(Math.random() * 10000),
          product,
          codice: product.code,
          descrizione: product.description,
          pesoGr: product.weightGr,
          um: product.unitMeasure,
          quantita: newQuantity,
          prezzo: parseFloat(product.price),
          imponibile: newQuantity * parseFloat(product.price),
          iva: parseInt(
            product.vatRate === 'FOUR' ? 4 :
              product.vatRate === 'TEN' ? 10 : 22
          ),
          confezione: product.packaging,
        };
        newItems.push(newItem);
      }
    } else {
      // Se quantità 0, rimuovi dall'ordine
      if (existingItemIndex >= 0) {
        newItems.splice(existingItemIndex, 1);
      }
    }

    updateOrderItems(newItems);
    console.log('Articoli ordine aggiornati:', newItems);
    updateData({ orderItems: newItems });
    setTimeout(() => validateStep(2, validations.products), 100);
  };

  // Funzioni carrello rimosse perché non più necessarie
  const isValid = validations.products(data);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="text-center mb-6 lg:mb-8 p-0">
        <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#fcfbd6' }}>
          <Package className="w-8 h-8 lg:w-10 lg:h-10" style={{ color: '#c48f75' }} />
        </div>
        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">
          Aggiungi Prodotti
        </h2>
        <p className="text-gray-600 text-sm lg:text-base">
          Seleziona i prodotti da aggiungere all'ordine
        </p>
      </div>

      {/* Tabella prodotti completa responsive e full width */}
      <div className="overflow-x-auto w-full rounded-xl p-2 sm:p-4 lg:p-6" style={{ backgroundColor: '#fcfbd6', maxHeight: '70vh', overflowY: 'auto' }}>
        <table className="min-w-full w-full divide-y divide-gray-200 text-xs sm:text-sm">
          <thead className="sticky top-0 z-20" style={{ backgroundColor: '#f7f7f7' }}>
            <tr className="bg-gray-100">
              <th className="px-2 py-2 font-bold text-gray-700 whitespace-nowrap">CODICE</th>
              <th className="px-2 py-2 font-bold text-gray-700 whitespace-nowrap">DESCRIZIONE</th>
              <th className="px-2 py-2 font-bold text-gray-700 whitespace-nowrap">PESO GR.</th>
              <th className="px-2 py-2 font-bold text-gray-700 whitespace-nowrap">U.M.</th>
              <th className="px-2 py-2 font-bold text-gray-700 whitespace-nowrap">QUANTITA'</th>
              <th className="px-2 py-2 font-bold text-gray-700 whitespace-nowrap">PREZZO</th>
              <th className="px-2 py-2 font-bold text-gray-700 whitespace-nowrap">IMPONIBILE</th>
              <th className="px-2 py-2 font-bold text-gray-700 whitespace-nowrap">IVA</th>
              <th className="px-2 py-2 font-bold text-gray-700 whitespace-nowrap">CONFEZIONE</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {productsLoading ? (
              <tr><td colSpan={11} className="text-center py-8">Caricamento prodotti...</td></tr>
            ) : (
              <>
                {/* Ordina tutti i prodotti per id, mostra categoria solo come intestazione se cambia */}
                {(() => {
                  const allProducts = (products || []).slice().sort((a, b) => {
                    if (typeof a.id === 'number' && typeof b.id === 'number') return a.id - b.id;
                    return String(a.id).localeCompare(String(b.id));
                  });
                  let lastCategory = null;
                  const rows = [];
                  allProducts.forEach(product => {
                    if (product.category !== lastCategory) {
                      rows.push(
                        <tr key={'cat-' + product.category} className="bg-yellow-100">
                          <td colSpan={11} className="font-bold text-gray-900 text-left px-2 py-2">{product.category || 'Altri'}</td>
                        </tr>
                      );
                      lastCategory = product.category;
                    }
                    rows.push(
                      <tr key={product.id}>
                        <td className="px-2 py-2 font-mono whitespace-nowrap">{product.code}</td>
                        <td className="px-2 py-2">{product.description}</td>
                        <td className="px-2 py-2 text-center whitespace-nowrap">{product.weightGr}</td>
                        <td className="px-2 py-2 text-center whitespace-nowrap">{product.unitMeasure}</td>
                        <td className="px-2 py-2 text-center whitespace-nowrap">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              type="button"
                              className="w-7 h-7 rounded-full bg-gray-200 hover:bg-yellow-200 text-lg font-bold flex items-center justify-center"
                              onClick={() => {
                                const q = quantities[product.id] || 0;
                                if (q > 0) handleQuantityChange(product, product.unitMeasure === 'KG.' ? q - 0.5 : q - 1);
                              }}
                            >-</button>
                            <input
                              type="number"
                              min="0"
                              step={product.unitMeasure === 'KG.' ? "0.5" : "1"}
                              value={quantities[product.id] || 0}
                              onChange={e => {
                                let inputValue = e.target.value;

                                // Se il campo è vuoto o contiene solo un punto/virgola, non processare ancora
                                if (inputValue === '' || inputValue === '.' || inputValue === ',') {
                                  return;
                                }

                                // Sostituisci la virgola con il punto per JavaScript
                                if (inputValue.includes(',')) {
                                  inputValue = inputValue.replace(',', '.');
                                }

                                // Se dopo la sostituzione abbiamo solo un punto, esci
                                if (inputValue === '.') {
                                  return;
                                }

                                let val = parseFloat(inputValue);

                                // Se parseFloat non riesce a convertire, esci senza fare nulla
                                if (isNaN(val)) {
                                  return;
                                }

                                if (val < 0) val = 0;

                                if (product.unitMeasure === 'KG.') {
                                  // Per KG, permetti solo valori interi o con .5
                                  val = Math.floor(val * 2) / 2;
                                } else {
                                  // Per altre unità di misura, mantieni solo numeri interi
                                  val = Math.floor(val);
                                }

                                handleQuantityChange(product, val);
                              }}
                              className="w-14 text-center border border-gray-300 rounded py-1 mx-1"
                            />
                            <button
                              type="button"
                              className="w-7 h-7 rounded-full bg-gray-200 hover:bg-yellow-200 text-lg font-bold flex items-center justify-center"
                              onClick={() => {
                                const q = quantities[product.id] || 0;
                                handleQuantityChange(product, product.unitMeasure === 'KG.' ? q + 0.5 : q + 1);
                              }}
                            >+</button>
                          </div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">€{(parseFloat(product.price) || 0).toFixed(2)}</td>
                        <td className="px-2 py-2 whitespace-nowrap">€{((quantities[product.id] || 0) * (parseFloat(product.price) || 0)).toFixed(2)}</td>
                        <td className="px-2 py-2 text-center whitespace-nowrap">{product.vatRate === 'FOUR' ? 4 : product.vatRate === 'TEN' ? 10 : 22}</td>
                        <td className="px-2 py-2 text-center whitespace-nowrap">{product.packaging}</td>
                        <td className="px-2 py-2 text-center whitespace-nowrap">{product.carton}</td>
                        <td className="px-2 py-2 text-center"></td>
                      </tr>
                    );
                  });
                  return rows;
                })()}
                {/* Riepilogo Totale */}
                <tr className="bg-yellow-200 font-bold text-lg">
                  <td colSpan={6} className="text-right px-2 py-3">Totale prodotti selezionati:</td>
                  <td className="px-2 py-3">
                    €{Object.entries(quantities).reduce((sum, [pid, qty]) => {
                      const prod = products.find(p => p.id === pid || p.id === Number(pid));
                      return sum + ((qty || 0) * (prod ? parseFloat(prod.price) || 0 : 0));
                    }, 0).toFixed(2)}
                  </td>
                  <td colSpan={4}></td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center mt-8">
        <button
          onClick={prevStep}
          className="flex items-center space-x-2 border border-gray-300 text-gray-700 px-4 sm:px-6 lg:px-8 py-3 rounded-xl font-medium hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Indietro</span>
        </button>
        <button
          onClick={() => {
            if (isValid) {
              validateStep(2, validations.products, data);
              nextStep();
            }
          }}
          disabled={!isValid}
          className={`flex items-center space-x-2 px-4 sm:px-6 lg:px-8 py-3 rounded-xl font-medium transition-all ${isValid
            ? 'text-white shadow-lg'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          style={isValid ? { backgroundColor: '#c48f75' } : {}}
          onMouseEnter={(e) => isValid && (e.target.style.backgroundColor = '#4c4849')}
          onMouseLeave={(e) => isValid && (e.target.style.backgroundColor = '#c48f75')}
        >
          <span>Continua</span>
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>

      {/* Validation Message */}
      {!isValid && (
        <div className="mt-4 flex items-center text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
          <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          <span>Aggiungi almeno un prodotto per continuare</span>
        </div>
      )}
    </div>
  );
};

export default ProductStep;